import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  doApiCheck,
  getAllDriveLabelsForDriveSearchQuery,
  getDomains,
  UserKeys,
} from '../../../api/users-api';
import { useUserSettings } from '../../../utils/use-user-settings';
import AvatarChip from '../../../shared/avatar-chip';
import DomainsAutoComplete from '../../../shared/domain-autocomplete';
import { documentTypeOptions } from '../../../shared/google-document-types';
import GroupsAutocomplete from '../../../shared/groups-autocomplete';
import OrganizationsAutoComplete from '../../../shared/organizations-autocomplete';
import ScopesAlert from '../../../shared/scopes-alert';
import UsersAutocomplete from '../../../shared/users-autocomplete';
import { Dispatch, SetStateAction, useState } from 'react';
import { DriveSearchForm } from './drive-search-page';
import { useQuery } from '@tanstack/react-query';
import { DatePicker } from '@mui/x-date-pickers';
import { isFeatureEnabled } from '../../../utils/feature-flags';
import {
  isAbleToSeeUIElement,
  DriveRoles,
} from '../../../utils/roles/client-roles';
import dayjs from 'dayjs';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

interface IncludedSources {
  value: string;
  type: string;
}

interface DriveSearchGridFiltersProps {
  control: Control<Partial<DriveSearchForm>>;
  watch: UseFormWatch<Partial<DriveSearchForm>>;
  errors: FieldErrors<Partial<DriveSearchForm>>;
  getValues: UseFormGetValues<Partial<DriveSearchForm>>;
  setValue: UseFormSetValue<Partial<DriveSearchForm>>;
  trigger: UseFormTrigger<Partial<DriveSearchForm>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  fromUserPage: boolean;
  userEmail: string;
}

export default function DriveSearchGridFilters({
  control,
  watch,
  errors,
  setValue,
  getValues,
  trigger,
  fromUserPage,
  userEmail,
}: DriveSearchGridFiltersProps) {
  const [sharedDocuments, setSharedDocuments] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [includedSources, setIncludedSources] = useState<IncludedSources[]>([]);
  const [includedSharedWithSources, setIncludedSharedWithSources] = useState<
    IncludedSources[]
  >([]);
  const [autoCompleteResponse, setAutoCompleteResponse] = useState(
    fromUserPage ? userEmail : ''
  );
  const [autoCompleteSharedWithResponse, setAutoCompleteSharedWithResponse] =
    useState('');
  const [newUserKey, setNewUserKey] = useState(new Date().getTime().toString());
  const [newGroupKey, setNewGroupKey] = useState(
    'Group - ' + new Date().getTime().toString()
  );
  const [newDomainKey, setNewDomainKey] = useState(
    'Domain - ' + new Date().getTime().toString()
  );
  const [newOrgKey, setNewOrgKey] = useState(
    'Org - ' + new Date().getTime().toString()
  );
  const { features, driveRoles } = useUserSettings();
  const driveLabelsFeatureEnabled = isFeatureEnabled(
    'driveLabelsRead',
    features
  );
  const canViewDriveLabels = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.CanViewDriveLabels.value
  );

  const [enableSharedWith, setEnableSharedWith] = useState(true);
  const [selectedLabelOption, setSelectedLabelOption] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [autoCompleteLabelResponse, setAutoCompleteLabelResponse] =
    useState('');

  const dateTypeOptions = [
    {
      value: 'modifiedDate',
      displayValue: 'Modified Date',
    },
    {
      value: 'lastViewedByMeDate',
      displayValue: 'Last Viewed by Me',
    },
  ];

  const dateTimeOptions = [
    {
      value: '>',
      displayValue: 'On or After',
    },
    {
      value: '<',
      displayValue: 'Before',
    },
  ];

  const { data: domains } = useQuery({
    queryKey: [UserKeys.Domains],
    queryFn: () => getDomains(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const {
    data: driveLabelsAccessGranted,
    isLoading: checkingDriveLabelsAccess,
  } = useQuery({
    queryKey: [UserKeys.AdminApiSettings, 'driveLabels'],
    queryFn: () => doApiCheck('driveLabels', null),
    staleTime: 0,
    enabled: driveLabelsFeatureEnabled && canViewDriveLabels,
  });

  const { data: driveLabelsResponse } = useQuery({
    ...getAllDriveLabelsForDriveSearchQuery(),
    enabled:
      driveLabelsFeatureEnabled &&
      canViewDriveLabels &&
      !!driveLabelsAccessGranted,
  });
  const driveLabels = (driveLabelsResponse?.labels ?? [])
    .map((entry) => entry.label)
    .filter(
      (l) =>
        l.lifecycle?.state === 'PUBLISHED' || l.lifecycle?.state === 'DISABLED'
    )
    .map((l) => ({
      id: l.id ?? '',
      title: l.properties?.title ?? l.id ?? '',
    }));

  const sources = watch('sources') || [];
  const sharedWithSources = watch('sharedWithSources') || [];
  const labels = watch('labels') || [];
  const includedLabels = (driveLabels || []).filter((l) =>
    labels.includes(l.id)
  );
  const isExactTitle = watch('isExactTitle')!;
  const sourceType = watch('sourceType')!;
  const docID = watch('docID') || '';

  function addSelection(source: string) {
    if (!source) return;
    const currentSources = getValues('sources') || [];
    if (currentSources.includes(source)) return;
    setValue('sources', [...currentSources, source]);
    setIncludedSources([
      ...includedSources,
      {
        value: source,
        type: getValues('sourceType')!,
      },
    ]);

    if (sharedWithSources.length > 0) {
      trigger('sharedWithSources');
    }
    if (includedSources.length === 0) {
      trigger('sources');
    }

    setNewUserKey(new Date().getTime().toString());
    setNewGroupKey('Group - ' + new Date().getTime().toString());
    setNewDomainKey('Domain - ' + new Date().getTime().toString());
    setNewOrgKey('Org - ' + new Date().getTime().toString());
    setAutoCompleteResponse('');
  }

  function addSharedWithSelection(source: string) {
    if (!source) return;
    const currentSharedWithSources = getValues('sharedWithSources') || [];
    if (currentSharedWithSources.includes(source)) return;
    setValue('sharedWithSources', [...currentSharedWithSources, source]);
    setIncludedSharedWithSources([
      ...includedSharedWithSources,
      {
        value: source,
        type: 'User',
      },
    ]);
    if (includedSharedWithSources.length === 0) {
      trigger('sharedWithSources');
    }
    setNewUserKey(new Date().getTime().toString());
    setAutoCompleteSharedWithResponse('');
  }

  function addLabelSelection(label: { id: string; title: string }) {
    const currentLabels = getValues('labels') || [];
    if (currentLabels.includes(label.id)) return;
    setValue('labels', [...currentLabels, label.id]);
    setSelectedLabelOption(null);
    setAutoCompleteLabelResponse('');
  }

  const allDocumentOptions = [
    { value: 'all', displayValue: 'All' },
    ...documentTypeOptions,
  ];

  return (
    <Card
      sx={{
        p: 1.5,
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <Stack gap={1.25}>
        {!fromUserPage && (
          <Stack>
            <FormControl>
              <FormLabel>
                {includedSources.length > 0 && !fromUserPage
                  ? 'Source Type (Only one source type is allowed)'
                  : 'Source Type'}
              </FormLabel>
              {!fromUserPage && (
                <Controller
                  name="sourceType"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      aria-labelledby="radio-buttons-group-label"
                      defaultValue="User"
                      {...field}
                      row
                    >
                      <FormControlLabel
                        value="User"
                        control={<Radio size="small" />}
                        label="User"
                        disabled={includedSources.length > 0}
                      />
                      <FormControlLabel
                        value="Group"
                        control={<Radio size="small" />}
                        label="Group"
                        disabled={includedSources.length > 0}
                      />
                      <FormControlLabel
                        value="OrgUnit"
                        control={<Radio size="small" />}
                        label="OU"
                        disabled={includedSources.length > 0}
                      />
                      <FormControlLabel
                        value="Domain"
                        control={<Radio size="small" />}
                        label="Domain"
                        disabled={includedSources.length > 0}
                      />
                    </RadioGroup>
                  )}
                />
              )}
            </FormControl>
            {!fromUserPage && (
              <Stack flexDirection={'row'} gap={1} alignItems="center">
                <Box sx={{ width: '100%' }}>
                  {sourceType === 'User' ? (
                    <Controller
                      name={'sources'}
                      rules={{
                        required: 'Source is required.',
                      }}
                      control={control}
                      render={() => (
                        <UsersAutocomplete
                          newKey={newUserKey}
                          autocompleteValue={autoCompleteResponse}
                          setAutoCompleteValue={setAutoCompleteResponse}
                          errors={!!errors.sources}
                          helperText={errors?.sources?.message || ' '}
                          onSelection={(value) => {
                            addSelection(value);
                          }}
                        />
                      )}
                    />
                  ) : sourceType === 'Group' ? (
                    <Controller
                      name={'sources'}
                      rules={{
                        required: 'Source is required.',
                      }}
                      control={control}
                      render={() => (
                        <GroupsAutocomplete
                          newKey={newGroupKey}
                          autocompleteValue={autoCompleteResponse}
                          setAutoCompleteValue={setAutoCompleteResponse}
                          errors={!!errors.sources}
                          helperText={errors?.sources?.message || ' '}
                          onSelection={(value) => {
                            addSelection(value);
                          }}
                        />
                      )}
                    />
                  ) : sourceType === 'Domain' ? (
                    <Controller
                      name={'sources'}
                      rules={{
                        required: 'Source is required.',
                      }}
                      control={control}
                      render={() => (
                        <DomainsAutoComplete
                          newKey={newDomainKey}
                          size="medium"
                          domains={domains || []}
                          setAutoCompleteValue={setAutoCompleteResponse}
                          hasError={!!errors.sources}
                          helperText={errors?.sources?.message || ' '}
                          onSelection={(value) => {
                            addSelection(value);
                          }}
                        />
                      )}
                    />
                  ) : (
                    <Controller
                      name={'sources'}
                      rules={{
                        required: 'Source is required.',
                      }}
                      control={control}
                      render={() => (
                        <OrganizationsAutoComplete
                          newKey={newOrgKey}
                          size="medium"
                          autoCompleteValue={autoCompleteResponse}
                          setAutoCompleteValue={setAutoCompleteResponse}
                          hasError={!!errors.sources}
                          helperText={errors?.sources?.message || ' '}
                          onSelection={(value) => {
                            addSelection(value);
                          }}
                        />
                      )}
                    />
                  )}
                </Box>
              </Stack>
            )}
            <Stack
              flexDirection={'row'}
              gap={1}
              sx={{
                width: 325,
                overflow: 'auto',
                mt: fromUserPage ? 1 : undefined,
              }}
            >
              {includedSources.map((source) => {
                return (
                  <Box key={source.value}>
                    <AvatarChip
                      email={source.value}
                      label={source.value}
                      type={
                        source.type as 'Group' | 'User' | 'Domain' | 'OrgUnit'
                      }
                      onDelete={
                        !fromUserPage
                          ? () => {
                              setIncludedSources(
                                includedSources.filter(
                                  (includedSource) =>
                                    includedSource.value !== source.value
                                )
                              );
                              setValue(
                                'sources',
                                sources.filter(
                                  (currentSource) =>
                                    currentSource != source.value
                                )
                              );
                              trigger('sharedWithSources');
                            }
                          : undefined
                      }
                    />
                  </Box>
                );
              })}
            </Stack>
          </Stack>
        )}

        {!fromUserPage && <Divider />}

        <Controller
          name="docID"
          control={control}
          render={({ field }) => (
            <TextField
              inputProps={{
                id: 'docID',
              }}
              InputLabelProps={{
                htmlFor: 'docID',
              }}
              label="Full Document ID"
              {...field}
              fullWidth
              sx={{ mt: 1 }}
              autoComplete="off"
              helperText="Searching with an ID ignores all other filters."
            />
          )}
        />
        <Divider />
        <Stack gap={2}>
          <Stack gap={1}>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack flexDirection="row" gap={1} flexGrow={1}>
                <Controller
                  name="title"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (isExactTitle === true && value!.length === 0) {
                        return 'Please add a title.';
                      } else {
                        return true;
                      }
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      inputProps={{
                        id: 'title',
                      }}
                      InputLabelProps={{
                        htmlFor: 'title',
                      }}
                      label="Document Title"
                      {...field}
                      fullWidth
                      autoComplete="off"
                      error={!!errors.title}
                      helperText={errors?.title?.message || ''}
                      disabled={docID.length > 0}
                    />
                  )}
                />
                <Controller
                  name="isExactTitle"
                  control={control}
                  render={({ field: props }) => (
                    <FormControlLabel
                      sx={{ alignSelf: 'flex-start' }}
                      control={
                        <Checkbox
                          inputProps={{
                            id: 'isExactTitle',
                          }}
                          {...props}
                          checked={props.value}
                          disabled={docID.length > 0}
                          onChange={(e) => {
                            props.onChange(e.target.checked);
                          }}
                        />
                      }
                      label="Exact"
                    />
                  )}
                />
              </Stack>
            </Stack>
            <Controller
              name="fullText"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'fullText',
                  }}
                  InputLabelProps={{
                    htmlFor: 'fullText',
                  }}
                  label="Full Text"
                  {...field}
                  fullWidth
                  autoComplete="off"
                  multiline
                  disabled={docID.length > 0}
                />
              )}
            />
          </Stack>
          <FormControl>
            <FormLabel>Shared Documents</FormLabel>
            <RadioGroup
              aria-labelledby="radio-buttons-group-label"
              defaultValue="All"
              name="radio-buttons-group"
              value={sharedDocuments}
              onChange={(event) => {
                if (event.target.value === 'all') {
                  setValue('onlyOwnedByMe', false);
                  setValue('onlySharedWithMe', false);
                  setSharedDocuments('all');
                  setEnableSharedWith(true);
                } else if (event.target.value === 'onlyOwnedByUser') {
                  setValue('onlyOwnedByMe', true);
                  setValue('onlySharedWithMe', false);
                  setSharedDocuments('onlyOwnedByUser');
                  setEnableSharedWith(false);
                  setIncludedSharedWithSources([]);
                } else {
                  setValue('onlyOwnedByMe', false);
                  setValue('onlySharedWithMe', true);
                  setSharedDocuments('onlySharedByUser');
                  setEnableSharedWith(true);
                }
              }}
              row
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                disabled={docID.length > 0}
                label="All"
              />
              <FormControlLabel
                value="onlyOwnedByUser"
                control={<Radio size="small" />}
                disabled={docID.length > 0}
                label="Only Owned"
              />
              <FormControlLabel
                value="onlySharedByUser"
                control={<Radio size="small" />}
                disabled={docID.length > 0}
                label="Only Shared"
              />
            </RadioGroup>
          </FormControl>

          {driveLabelsFeatureEnabled &&
            canViewDriveLabels &&
            !driveLabelsAccessGranted &&
            !checkingDriveLabelsAccess && (
              <FormControl>
                <FormLabel>Labels</FormLabel>
                <Box sx={{ mt: 1 }}>
                  <ScopesAlert apiKey="driveLabels" />
                </Box>
              </FormControl>
            )}

          {driveLabelsFeatureEnabled &&
            canViewDriveLabels &&
            !!driveLabelsAccessGranted && (
              <FormControl>
                <FormLabel>Labels</FormLabel>
                <Stack
                  flexDirection={'row'}
                  gap={1}
                  alignItems="center"
                  sx={{ mt: 1 }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Controller
                      name={'labels'}
                      control={control}
                      render={() => (
                        <Autocomplete<{ id: string; title: string }>
                          value={selectedLabelOption}
                          onChange={(_, value) => {
                            setSelectedLabelOption(value);
                            if (value) {
                              setAutoCompleteLabelResponse(value.title);
                              addLabelSelection(value);
                            }
                          }}
                          options={(driveLabels || []).sort((a, b) =>
                            a.title.localeCompare(b.title)
                          )}
                          disabled={docID.length > 0}
                          getOptionLabel={(option) => option.title}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          autoComplete
                          autoHighlight
                          inputValue={autoCompleteLabelResponse}
                          onInputChange={(_, value) => {
                            setAutoCompleteLabelResponse(value);
                            if (!value) setSelectedLabelOption(null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search for a label"
                              size="small"
                              sx={{ width: '100%' }}
                            />
                          )}
                        />
                      )}
                    />
                  </Box>
                </Stack>
                <Stack
                  flexDirection={'row'}
                  gap={1}
                  sx={{
                    width: 325,
                    overflow: 'auto',
                    mt: 1,
                  }}
                >
                  {includedLabels.map((label) => (
                    <Tooltip key={label.id} title={label.title}>
                      <Chip
                        label={label.title}
                        variant="filled"
                        size="small"
                        onDelete={() => {
                          setValue(
                            'labels',
                            labels.filter((l) => l !== label.id)
                          );
                        }}
                        sx={{
                          maxWidth: 200,
                          flexShrink: 0,
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </FormControl>
            )}

          <FormControl error={!!errors.sharedWithSources}>
            <FormLabel>Shared With</FormLabel>
            <Stack
              flexDirection={'row'}
              gap={1}
              alignItems="center"
              sx={{ mt: 1 }}
            >
              <Controller
                name={'sharedWithSources'}
                rules={{
                  validate: (value) => {
                    const currentSources = getValues('sources') || [];
                    if (
                      currentSources.length === 0 &&
                      value &&
                      value.length > 0
                    ) {
                      return 'Please select a source type before applying filters.';
                    }
                    return true;
                  },
                }}
                control={control}
                render={() => (
                  <Box sx={{ width: '100%' }}>
                    <UsersAutocomplete
                      disabled={!enableSharedWith}
                      newKey={newUserKey}
                      autocompleteValue={autoCompleteSharedWithResponse}
                      setAutoCompleteValue={setAutoCompleteSharedWithResponse}
                      errors={!!errors.sharedWithSources}
                      freeSolo={true}
                      onSelection={(value) => {
                        addSharedWithSelection(value);
                      }}
                    />
                  </Box>
                )}
              />
            </Stack>
            {errors.sharedWithSources ? (
              <FormHelperText sx={{ m: 1 }}>
                {errors.sharedWithSources.message}
              </FormHelperText>
            ) : null}
            <Stack
              flexDirection={'row'}
              gap={1}
              sx={{
                width: 325,
                overflow: 'auto',
              }}
            >
              {includedSharedWithSources.map((source) => {
                return (
                  <Box key={source.value} sx={{ mt: 1 }}>
                    <AvatarChip
                      email={source.value}
                      label={source.value}
                      type={source.type as 'User'}
                      onDelete={() => {
                        setIncludedSharedWithSources(
                          includedSharedWithSources.filter(
                            (sharedWithSource) =>
                              sharedWithSource.value !== source.value
                          )
                        );
                        setValue(
                          'sharedWithSources',
                          sharedWithSources.filter(
                            (currentSource) => currentSource != source.value
                          )
                        );
                        trigger('sharedWithSources');
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </FormControl>
          <FormControl>
            <FormLabel>Category Filter</FormLabel>
            <RadioGroup
              aria-labelledby="radio-buttons-category-label"
              defaultValue="All"
              name="radio-buttons-category"
              value={categoryFilter}
              onChange={(event) => {
                if (event.target.value === 'all') {
                  setValue('trashed', false);
                  setValue('starred', false);
                  setCategoryFilter('all');
                } else if (event.target.value === 'trashed') {
                  setValue('trashed', true);
                  setValue('starred', false);
                  setCategoryFilter('trashed');
                } else {
                  setValue('trashed', false);
                  setValue('starred', true);
                  setCategoryFilter('starred');
                }
              }}
              row
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" />}
                disabled={docID.length > 0}
                label="All"
              />
              <FormControlLabel
                value="trashed"
                control={<Radio size="small" />}
                disabled={docID.length > 0}
                label="Is Trashed"
              />
              <FormControlLabel
                value="starred"
                control={<Radio size="small" />}
                disabled={docID.length > 0}
                label="Is Starred"
              />
            </RadioGroup>
          </FormControl>
          <Controller
            name="mimeType"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'mimeType',
                }}
                InputLabelProps={{
                  htmlFor: 'mimeType',
                }}
                label="Document Type"
                select
                {...field}
                fullWidth
                autoComplete="off"
                disabled={docID.length > 0}
              >
                {allDocumentOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Stack flexDirection="row" gap={2}>
            <Controller
              name="dateType"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'dateType',
                  }}
                  InputLabelProps={{
                    htmlFor: 'dateType',
                  }}
                  label="Date"
                  select
                  disabled={docID.length > 0}
                  {...field}
                  autoComplete="off"
                  fullWidth
                >
                  {dateTypeOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="time"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'time',
                  }}
                  InputLabelProps={{
                    htmlFor: 'time',
                  }}
                  disabled={docID.length > 0}
                  label="Time"
                  select
                  {...field}
                  autoComplete="off"
                  fullWidth
                >
                  {dateTimeOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <Controller
            name="day"
            control={control}
            render={({ field }) => (
              <DatePicker
                {...field}
                label="Day"
                sx={{ width: '100%' }}
                disabled={docID.length > 0}
                value={field.value != null ? dayjs(field.value) : null}
                onChange={(date) => {
                  if (!date || !date.isValid()) {
                    field.onChange(null);
                  } else {
                    field.onChange(date.valueOf());
                  }
                }}
              />
            )}
          />
        </Stack>
      </Stack>
    </Card>
  );
}
